import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import SvgIcon from '@/components/SvgIcon';
import { RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';

interface ActionMenuProps {
    onEdit?: () => void;
    onDelete?: () => void;
    onDuplicate?: () => void;
}

const ActionMenu = ({ onEdit, onDelete, onDuplicate }: ActionMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPos({
                top: rect.top + rect.height / 2,
                left: rect.left - 140 - 12,
            });
        }
        setIsOpen(!isOpen);
    };

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (
            menuRef.current &&
            !menuRef.current.contains(event.target as Node) &&
            buttonRef.current &&
            !buttonRef.current.contains(event.target as Node)
        ) {
            setIsOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    const handleAction = (action?: () => void) => {
        if (action) action();
        setIsOpen(false);
    };

    return (
        <div className="relative inline-block text-left">
            <div
                ref={buttonRef}
                className="cursor-pointer p-2 rounded-full hover:bg-gray-100 flex items-center justify-center w-8 h-8"
                onClick={toggleMenu}
            >
                <div className="flex flex-col gap-[3px]">
                    <div className="w-[3px] h-[3px] rounded-full bg-[#C6C8C9]" />
                    <div className="w-[3px] h-[3px] rounded-full bg-[#C6C8C9]" />
                    <div className="w-[3px] h-[3px] rounded-full bg-[#C6C8C9]" />
                </div>
            </div>

            {isOpen &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="fixed w-[140px] rounded-[16px] shadow-[0_4px_20px_0_rgba(0,0,0,0.1)] bg-white z-[9999] overflow-visible"
                        style={{
                            top: menuPos.top,
                            left: menuPos.left,
                            transform: 'translateY(-50%)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Triangle Arrow - centered vertically */}
                        <div
                            className="absolute top-1/2 -translate-y-1/2 -right-[6px] w-[12px] h-[12px] bg-white rotate-45"
                            style={{ boxShadow: '2px -2px 4px rgba(0,0,0,0.04)' }}
                        />

                        <div className="py-[8px] flex flex-col relative z-10 gap-[4px]">
                            {onDuplicate && (
                                <>
                                    <div
                                        className={`flex items-center px-[16px] py-[9px] mx-[8px] rounded-[10px] cursor-pointer gap-[16px] ${
                                            hoveredItem === 'duplicate' ? 'bg-[#EBF4FF]' : ''
                                        }`}
                                        onClick={() => handleAction(onDuplicate)}
                                        onMouseEnter={() => setHoveredItem('duplicate')}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        <SvgIcon
                                            name="SvgCopy"
                                            size={18}
                                            stroke={hoveredItem === 'duplicate' ? '#3182F6' : colors.Text_1}
                                            fill={colors.trans}
                                            strokeWidth={1}
                                        />
                                        <RegularText
                                            fontSize={16}
                                            lineHeight={24}
                                            color={hoveredItem === 'duplicate' ? '#3182F6' : colors.Text_1}
                                        >
                                            복제
                                        </RegularText>
                                    </div>
                                    <div className="h-[0.5px] bg-[#F0F0F0] mx-[16px]" />
                                </>
                            )}

                            {onEdit && (
                                <>
                                    <div
                                        className={`flex items-center px-[16px] py-[14px] mx-[8px] rounded-[10px] cursor-pointer gap-[16px] ${
                                            hoveredItem === 'edit' ? 'bg-[#EBF4FF]' : ''
                                        }`}
                                        onClick={() => handleAction(onEdit)}
                                        onMouseEnter={() => setHoveredItem('edit')}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        <SvgIcon
                                            name="SvgEdit"
                                            size={18}
                                            stroke={hoveredItem === 'edit' ? '#3182F6' : colors.Text_1}
                                            fill={colors.trans}
                                            strokeWidth={1}
                                        />
                                        <RegularText
                                            fontSize={16}
                                            lineHeight={24}
                                            color={hoveredItem === 'edit' ? '#3182F6' : colors.Text_1}
                                        >
                                            수정
                                        </RegularText>
                                    </div>
                                    <div className="h-[0.5px] bg-[#F0F0F0] mx-[16px]" />
                                </>
                            )}

                            <div
                                className={`flex items-center px-[16px] py-[14px] mx-[8px] rounded-[10px] cursor-pointer gap-[16px] ${hoveredItem === 'delete' ? 'bg-[#EBF4FF]' : ''}`}
                                onClick={() => handleAction(onDelete)}
                                onMouseEnter={() => setHoveredItem('delete')}
                                onMouseLeave={() => setHoveredItem(null)}
                            >
                                <SvgIcon
                                    name="SvgTrash"
                                    size={18}
                                    fill={hoveredItem === 'delete' ? '#3182F6' : colors.Text_1}
                                />
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color={hoveredItem === 'delete' ? '#3182F6' : colors.Text_1}
                                >
                                    삭제
                                </RegularText>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default ActionMenu;
